// @ts-ignore
import React, { useEffect, useMemo } from 'react';

import {
	ServerSideRequestStatus,
	UserProfile,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { checkFeatureFlagGate } from '@wac-web/utils/feature-flags/check-feature-flag';
import { featureFlags as featureFlagsManifest } from '@wac-web/utils/feature-flags/feature-flag-manifest';
import { UserLocale } from '@wac-web/utils/useGeoCurrency';
import { useIntl } from 'react-intl';

import {
	getSSRAutoScript,
	getThemeHtmlAttrs,
	getThemeStyles,
	setGlobalTheme,
} from '@atlaskit/tokens';
import type AnalyticsWebClient from '@atlassiansox/analytics-web-client';

import { parseRoute } from './fastshift-portal-routes';
import { Layout, PageProps } from './layout';

export type ParcelManifestModuleType = 'global' | 'esmodule';
export type ParcelManifestLoadType = 'sync' | 'async' | 'defer';

export type ParcelEntryManifest = {
	[key in ParcelManifestModuleType]: {
		[key in ParcelManifestLoadType]: string[];
	};
};

type PageWrapperProps = {
	analyticsWebClient?: AnalyticsWebClient;
	location: Location;
	manifest?: ParcelEntryManifest;
	inlineCss?: string;
	userProfile?: UserProfile;
	userProfileStatus?: ServerSideRequestStatus;
	countryCode?: UserLocale;
	pureClientMode?: boolean;
	themeStyles?: Awaited<ReturnType<typeof getThemeStyles>>;
	track?: string;
	hasCloudSessionTokenCookie?: boolean;
	children: React.ComponentType<PageProps>;
	userAgent?: string;
};

const SUPPORTED_LOCALES = [
	{ hrefLang: 'ja', langRoute: 'ja' },
	{ hrefLang: 'fr', langRoute: 'fr' },
	{ hrefLang: 'de', langRoute: 'de' },
	{ hrefLang: 'es', langRoute: 'es' },
	{ hrefLang: 'pt-BR', langRoute: 'br' },
	{ hrefLang: 'zh-Hans', langRoute: 'zh' },
	{ hrefLang: 'ko', langRoute: 'ko' },
	{ hrefLang: 'ru', langRoute: 'ru' },
	{ hrefLang: 'pl', langRoute: 'pl' },
	{ hrefLang: 'it', langRoute: 'it' },
	{ hrefLang: 'nl', langRoute: 'nl' },
];

const smartlingContextTrackerInit = `
    window.SmartlingContextTracker.init({
        orgId: "37bF-QEPm5S9skqlCM0VvQ",
        overrideContextOlderThanDays: 1
    });
`;

const assetPrefix =
	process.env['VERSION'] === 'local' ? '' : `${process.env['ASSET_PREFIX'] || ''}`;

const toAssetPath = (name: string) => `${assetPrefix}/assets/${name}`;

const excludeHardcodedScripts = (src: string) => !src.endsWith('atl-onetrust-wrapper.min.js'); // statically included by PageWrapper

/**
 * if there is a corresponding esmodule defer or sync script in the manifest,
 * treat the given script as a nomodule fallback.
 */
const isNoModuleFallback = (script: string, manifest: ParcelEntryManifest): boolean => {
	const basename = script.split(/\..+\.js$/)[0] ?? '';
	if (basename === '') {
		return false;
	}
	return (
		manifest?.esmodule.defer.some((es) => es.startsWith(basename)) ||
		manifest?.esmodule.sync.some((es) => es.startsWith(basename))
	);
};

const ThemeStyles = ({
	themeStyles,
	location,
}: {
	themeStyles: Awaited<ReturnType<typeof getThemeStyles>>;
	location: Location;
}) => {
	const isPreloadingAtlaskitStylesEnabled = checkFeatureFlagGate(
		featureFlagsManifest.preloadAtlaskitStyles,
	);

	if (!isPreloadingAtlaskitStylesEnabled || assetPrefix === '') {
		// Original approach with render-blocking stylesheets
		return (
			<>
				{(themeStyles || []).map((theme) => (
					<link
						rel="stylesheet"
						key={theme.id}
						{...theme.attrs}
						href={`${assetPrefix}/assets/atlaskit-tokens_${theme.id}.css`}
					/>
				))}
			</>
		);
	}

	let domain: string | undefined;
	let hostname: string | undefined;
	let isCrossOrigin = false;

	try {
		domain = new URL(assetPrefix).origin;
		hostname = new URL(assetPrefix).hostname;
		// Check if the asset domain is different from current domain
		isCrossOrigin = location.origin !== domain;
	} catch (error) {
		console.error('Invalid AssetPrefix URL:', assetPrefix, error);
	}

	// Optimized approach with preconnect, dns-prefetch and preload
	return (
		<>
			{themeStyles && themeStyles.length > 0 && domain && hostname && (
				<>
					<link rel="dns-prefetch" href={'//' + hostname} />
					<link
						rel="preconnect"
						href={domain}
						{...(isCrossOrigin && {
							crossOrigin: 'anonymous',
						})}
					/>
				</>
			)}
			{(themeStyles || []).map((theme) => (
				<React.Fragment key={theme.id}>
					<link
						rel="preload"
						as="style"
						href={`${assetPrefix}/assets/atlaskit-tokens_${theme.id}.css`}
						{...(isCrossOrigin && { crossOrigin: 'anonymous' })}
					/>
					<link
						rel="stylesheet"
						{...theme.attrs}
						href={`${assetPrefix}/assets/atlaskit-tokens_${theme.id}.css`}
						{...(isCrossOrigin && { crossOrigin: 'anonymous' })}
					/>
				</React.Fragment>
			))}
		</>
	);
};

// eslint-disable-next-line complexity
export const PageWrapper = (props: PageWrapperProps) => {
	const {
		analyticsWebClient,
		location,
		manifest,
		userProfile,
		userProfileStatus,
		countryCode,
		pureClientMode,
		themeStyles,
		hasCloudSessionTokenCookie,
		userAgent,
	} = props;

	const { inlineCss } = props;

	const intl = useIntl();

	const baseUrl = 'https://www.fastshift.atlassian.com';
	const { locale, path } = parseRoute(location);

	// @ts-ignore
	const route = path !== '' ? '/' + path : '';

	const localePrefix = locale === 'en' ? '' : `/${locale}`;

	const themePrefs = useMemo(() => ({ colorMode: 'light' }) as const, []);
	const themeAttrs = getThemeHtmlAttrs(themePrefs);
	const ssrAutoScript = getSSRAutoScript(themePrefs.colorMode);

	const isStagingEnvironment = process.env['NODE_ENV'] === 'staging';
	const isInstrumentedLocale = locale === 'zz';
	const shouldSendToSmartling = isStagingEnvironment && isInstrumentedLocale;

	useEffect(() => {
		// Theme config for client-only rendering
		setGlobalTheme(themePrefs);
	}, [themePrefs]);

	let title, description, iosSmartBannerMeta;

	switch (route) {
		default:
			title = intl.formatMessage({
				id: 'fastshift-portal.title',
				defaultMessage: 'FastShift Portal | Atlassian',
			});
			description = intl.formatMessage({
				id: 'fastshift-portal.description',
				defaultMessage:
					"FastShift Portal is Atlassian's platform for managing, tracking, and accelerating migrations, bringing customers, partners, and teams together in one place.",
			});
			break;
	}

	if (pureClientMode) {
		return (
			<>
				<style suppressHydrationWarning dangerouslySetInnerHTML={{ __html: inlineCss || '' }} />
				<link rel="canonical" href={`${baseUrl}${route}`} />
				<link rel="alternate" href={`${baseUrl}${route}`} hrefLang="x-default" />
				{SUPPORTED_LOCALES.map(({ hrefLang, langRoute }) => (
					<link
						key={hrefLang}
						rel="alternate"
						href={`${baseUrl}/${langRoute}${route}`}
						hrefLang={hrefLang}
					/>
				))}
				<Layout
					analyticsWebClient={analyticsWebClient || null}
					userProfile={userProfile}
					location={location}
					countryCode={countryCode}
					userAgent={userAgent}
				>
					{props.children}
				</Layout>
			</>
		);
	}

	return (
		<html data-source="uip" lang={intl.locale} {...themeAttrs}>
			<head>
				<meta charSet="utf-8" />
				<title>
					{
						// @ts-ignore
						title !== undefined && title.length > 0
							? // @ts-ignore
								title.charAt(0).toUpperCase() + title.slice(1)
							: title
					}
				</title>
				{description !== undefined && <meta name="description" content={description} />}
				<meta name="viewport" content="width=device-width, initial-scale=1" />
				<meta httpEquiv="X-UA-Compatible" content="IE=edge, chrome=1" />
				<meta name="author" content="Atlassian" />
				{iosSmartBannerMeta && <meta name="apple-itunes-app" content={iosSmartBannerMeta} />}

				<style
					suppressHydrationWarning
					dangerouslySetInnerHTML={{ __html: inlineCss || '' }}
					id="ssr_style"
				/>
				<link rel="canonical" href={`${baseUrl}${localePrefix}${route}`} />
				<link rel="alternate" href={`${baseUrl}${route}`} hrefLang="x-default" />
				{/* tags for Atlassian Sans font https://hello.atlassian.net/wiki/spaces/DST/pages/4777151801/Using+Atlassian+Sans+and+Atlassian+Mono */}
				<link rel="preconnect" href="https://ds-cdn.prod-east.frontend.public.atl-paas.net" />
				<link
					rel="preload"
					href="https://ds-cdn.prod-east.frontend.public.atl-paas.net/assets/fonts/atlassian-sans/v4/AtlassianSans-latin.woff2"
					as="font"
					type="font/woff2"
					// @ts-ignore
					crossOrigin
				/>
				<link
					rel="preload stylesheet"
					href="https://ds-cdn.prod-east.frontend.public.atl-paas.net/assets/font-rules/v6/atlassian-fonts.css"
				/>
				{SUPPORTED_LOCALES.map(({ hrefLang, langRoute }) => (
					<link
						key={hrefLang}
						rel="alternate"
						href={`${baseUrl}/${langRoute}${route}`}
						hrefLang={hrefLang}
					/>
				))}
				{<ThemeStyles themeStyles={themeStyles ?? []} location={location} />}

				{ssrAutoScript ? <script dangerouslySetInnerHTML={{ __html: ssrAutoScript }} /> : null}
				<script
					dangerouslySetInnerHTML={{
						__html: `
                            // Polyfill for webkit messageHandlers to prevent OneTrust errors
                            // This fixes: "undefined is not an object (evaluating 'window.webkit.messageHandlers')"
                            if (typeof window !== 'undefined' && !window.webkit) {
                                window.webkit = { messageHandlers: {} };
                            }
                        `,
					}}
				/>
				<script
					id="onetrust"
					type="text/javascript"
					src={process.env['ONETRUST_SCRIPT_URL']}
					data-domain={process.env['ONETRUST_SCRIPT_GUID']}
					data-test={process.env['ONETRUST_DATA_TEST']}
					data-geofence="false"
					data-sync="true"
					defer
				/>
				{shouldSendToSmartling && (
					<>
						<script type="text/javascript" src="//d2c7xlmseob604.cloudfront.net/tracker.min.js" />
						<script
							dangerouslySetInnerHTML={{
								__html: smartlingContextTrackerInit,
							}}
						/>
					</>
				)}

				{manifest?.['global'].sync
					.filter(excludeHardcodedScripts)
					.filter((src) => src.endsWith('.js'))
					.map((src, i) => (
						<script key={i} src={toAssetPath(src)} />
					))}

				{manifest?.['global'].defer
					.filter(excludeHardcodedScripts)
					.filter((src) => src.endsWith('.js'))
					.map((src, i) =>
						isNoModuleFallback(src, manifest) ? (
							<script key={i} defer noModule src={toAssetPath(src)} />
						) : (
							<script key={i} defer src={toAssetPath(src)} />
						),
					)}

				{manifest?.['global'].async
					.filter(excludeHardcodedScripts)
					.filter((src) => src.endsWith('.js'))
					.map((src, i) => (
						<script key={i} async src={toAssetPath(src)} />
					))}

				{manifest?.esmodule.sync
					.filter(excludeHardcodedScripts)
					.filter((src) => src.endsWith('.js'))
					.map((src, i) => (
						<script key={i} type="module" src={toAssetPath(src)} />
					))}

				{manifest?.esmodule.defer
					.filter(excludeHardcodedScripts)
					.filter((src) => src.endsWith('.js'))
					.map((src, i) => (
						<script key={i} type="module" src={toAssetPath(src)} />
					))}

				{manifest?.esmodule.async
					.filter(excludeHardcodedScripts)
					.filter((src) => src.endsWith('.js'))
					.map((src, i) => (
						<script key={i} async type="module" src={toAssetPath(src)} />
					))}
			</head>
			<body>
				<div id="fastshift-portal-root">
					<Layout
						analyticsWebClient={analyticsWebClient || null}
						location={location}
						userProfile={userProfile}
						userProfileStatus={userProfileStatus}
						countryCode={countryCode}
						hasCloudSessionTokenCookie={hasCloudSessionTokenCookie}
						userAgent={userAgent}
					>
						{props.children}
					</Layout>
				</div>
			</body>
		</html>
	);
};
